import { useState } from 'react';
import { PasswordInput, Button, Group, Stack } from '@mantine/core';
import { Key } from '@phosphor-icons/react';
import { useChangeAdminPassword } from '../../hooks/admin/useAdminUsers';
import { useAuthThrottle } from '../../hooks/useAuthThrottle';
import { validateNewPassword, isStrongPassword } from '../../utils/passwordPolicy';
import { parseApiError } from '../../utils/apiError';
import { PasswordPolicyChecklist, ThrottleAlert, ErrorAlert } from './AuthFeedback';

interface AdminChangePasswordFormProps {
  onSuccess: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
}

/** PATCH /v1/admin/auth/password — the hook stores the new token pair returned by the BE. */
export function AdminChangePasswordForm({
  onSuccess,
  onCancel,
  submitLabel = 'Update Password',
  cancelLabel = 'Cancel',
}: AdminChangePasswordFormProps) {
  const changePassword = useChangeAdminPassword();
  const throttle = useAuthThrottle();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateNewPassword({ currentPassword, newPassword, confirmPassword });
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          onSuccess();
        },
        onError: (err) => {
          if (throttle.handleError(err)) {
            setError(null);
            return;
          }
          setError(parseApiError(err, 'Failed to change password').message);
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={14}>
        <ThrottleAlert throttle={throttle} />
        <ErrorAlert message={error} />

        <PasswordInput
          label="Current Password"
          placeholder="Enter current password"
          required
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.currentTarget.value)}
        />
        <PasswordInput
          label="New Password"
          placeholder="At least 12 characters"
          required
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.currentTarget.value)}
        />
        {newPassword && <PasswordPolicyChecklist password={newPassword} />}
        <PasswordInput
          label="Confirm New Password"
          placeholder="Re-enter new password"
          required
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.currentTarget.value)}
          error={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : undefined}
        />

        <Group justify="flex-end" mt={8}>
          {onCancel && (
            <Button variant="subtle" onClick={onCancel} style={{ color: 'var(--text-secondary)' }}>
              {cancelLabel}
            </Button>
          )}
          <Button
            type="submit"
            loading={changePassword.isPending}
            disabled={throttle.isLocked || !isStrongPassword(newPassword)}
            leftSection={<Key size={14} />}
            color="amber"
            style={{ borderRadius: 4, fontWeight: 600 }}
          >
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
